/**
 * @file Static prop collider factory (issue #44).
 *
 * The dungeon's collidable props — columns, chests, sarcophagi, braziers — reuse
 * the {@link ./Wall.js} pattern: no `Renderable` (the builder draws them as
 * instanced meshes), just a `Transform` and a static `Collider` so the physics
 * system treats them like anything else. The only knob a prop adds over a wall
 * is whether it stops bullets: a column is real cover, a brazier is not.
 */

import { Transform, Collider } from '../components/index.js';
import { Layer } from '../config/Layers.js';

/**
 * Creates an immovable prop collider.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {number} x Centre X, world units.
 * @param {number} z Centre Z, world units.
 * @param {number} halfX Half-extent along X.
 * @param {number} halfZ Half-extent along Z.
 * @param {boolean} [blocksProjectiles] When true the collider also stops bullets.
 * @param {string} [name] Debug label.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createProp(ctx, x, z, halfX, halfZ, blocksProjectiles = false, name = 'prop') {
  const entity = ctx.world.createEntity(name);

  // Bodies always collide; bullet layers are added only for real cover so a
  // brazier or gravestone never eats a shot the player expected to sail past.
  let mask = Layer.PLAYER | Layer.ENEMY;
  if (blocksProjectiles) mask |= Layer.PLAYER_BULLET | Layer.ENEMY_BULLET;

  entity.add(new Transform(x, 0, z));
  entity.add(new Collider({ halfX, halfZ, layer: Layer.WALL, mask, isStatic: true }));

  return entity;
}
